import type { UserSession } from '@/api/client';

import { useAuth } from './use-auth';

/**
 * Website-config feature keys read from `UserSession.website_config`.
 */
export const WEBSITE_FEATURES = {
  DISPLAY_BUILDING_NAME: 'display_building_name',
  CHECK_ASSIGNMENT: 'enable_check_assignment',
} as const;

export type WebsiteFeature =
  (typeof WEBSITE_FEATURES)[keyof typeof WEBSITE_FEATURES];

const WEBSITE_FEATURE_TO_KEY: Record<WebsiteFeature, string> = {
  [WEBSITE_FEATURES.DISPLAY_BUILDING_NAME]: 'display_building_name',
  [WEBSITE_FEATURES.CHECK_ASSIGNMENT]: 'enable_check_assignment',
};

function getWebsiteConfigValue(
  user: UserSession | null | undefined,
  key: string
): unknown {
  const websiteConfig = user?.website_config;
  if (!websiteConfig || typeof websiteConfig !== 'object') {
    return undefined;
  }
  return (websiteConfig as Record<string, unknown>)[key];
}

/**
 * Pure helper: whether the given website-config feature is enabled.
 * Returns true only when the flag is explicitly true.
 */
export function getWebsiteFeatureEnabled(
  user: UserSession | null | undefined,
  feature: WebsiteFeature
): boolean {
  return getWebsiteConfigValue(user, WEBSITE_FEATURE_TO_KEY[feature]) === true;
}

/**
 * Hook: whether the given website-config feature is enabled.
 */
export function useWebsiteFeature(feature: WebsiteFeature): boolean {
  const { user } = useAuth();
  return getWebsiteFeatureEnabled(user, feature);
}

/**
 * Hook: whether each of the given website-config features is enabled.
 */
export function useWebsiteFeatures(
  features: WebsiteFeature[]
): Record<WebsiteFeature, boolean> {
  const { user } = useAuth();
  const result = {} as Record<WebsiteFeature, boolean>;
  for (const feature of features) {
    result[feature] = getWebsiteFeatureEnabled(user, feature);
  }
  return result;
}
